import { useState } from 'react';
import { filesApi } from '../../../api/files.js';
import { engagementsApi } from '../../../api/engagements.js';
import { FileInput } from '../../../components/common/FileInput.jsx';
import { ErrorAlert } from '../../../components/common/ErrorAlert.jsx';

export function FilesTab({ engagement, onUpdate }) {
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState(null);

  const uploadHeadshot = async (file) => {
    setUploading('headshot');
    try {
      const { id } = await filesApi.upload(file);
      const updated = await engagementsApi.update(engagement.id, { headshot: { fileId: id } });
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(null);
    }
  };

  const uploadAgreement = async (file) => {
    setUploading('agreement');
    try {
      const { id } = await filesApi.upload(file);
      const updated = await engagementsApi.update(engagement.id, {
        agreementFile: { fileId: id },
        agreementStatus: 'Received',
        agreementReceivedDate: new Date().toISOString().slice(0, 10),
      });
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(null);
    }
  };

  const headshot = engagement.headshot;
  const agreement = engagement.agreementFile;

  return (
    <div className="stack">
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <section>
        <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-3)' }}>Headshot</h3>
        {headshot?.fileId ? (
          <div className="inline">
            <img
              src={filesApi.getUrl(headshot.fileId)}
              alt="Speaker headshot"
              style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}
            />
            <div className="stack-2">
              <a href={filesApi.getUrl(headshot.fileId)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                View / download
              </a>
              <FileInput
                onFile={uploadHeadshot}
                accept="image/jpeg,image/png,image/webp"
                label={uploading === 'headshot' ? 'Uploading…' : 'Replace headshot'}
                hint="JPG, PNG, or WebP"
              />
            </div>
          </div>
        ) : (
          <FileInput
            onFile={uploadHeadshot}
            accept="image/jpeg,image/png,image/webp"
            label={uploading === 'headshot' ? 'Uploading…' : 'Upload headshot'}
            hint="JPG, PNG, or WebP — drag and drop or click to browse"
          />
        )}
      </section>

      <hr className="divider" />

      <section>
        <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-3)' }}>Signed agreement</h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
          Agreement status: <strong>{engagement.agreementStatus}</strong>
          {engagement.agreementSentDate && ` · Sent ${engagement.agreementSentDate}`}
          {engagement.agreementReceivedDate && ` · Received ${engagement.agreementReceivedDate}`}
        </p>
        {agreement?.fileId ? (
          <div className="inline">
            <a href={filesApi.getUrl(agreement.fileId)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
              📄 Download agreement
            </a>
            <FileInput
              onFile={uploadAgreement}
              accept="application/pdf"
              label={uploading === 'agreement' ? 'Uploading…' : 'Replace agreement'}
              hint="PDF only"
            />
          </div>
        ) : (
          <FileInput
            onFile={uploadAgreement}
            accept="application/pdf"
            label={uploading === 'agreement' ? 'Uploading…' : 'Upload signed agreement'}
            hint="PDF only — drag and drop or click to browse"
          />
        )}
      </section>
    </div>
  );
}
